import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui';
import { HotelLogoCarousel } from '../components/HotelLogoCarousel';
import { 
  MessageCircle, 
  Brain, 
  Users, 
  Globe, 
  Heart,
  Palette,
  BookOpen
} from 'lucide-react';

export function MeetScout() {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    const sections = document.querySelectorAll('.story-section');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  // Track which sections have been seen
  const [seenSections, setSeenSections] = useState<string[]>([]);

  // Update seen sections when active section changes
  useEffect(() => {
    if (activeSection && !seenSections.includes(activeSection)) {
      setSeenSections(prev => [...prev, activeSection]);
    }
  }, [activeSection, seenSections]);

  const storyBlocks = [
    {
      id: 'blueprint',
      title: 'From a Blueprint to a Colleague',
      icon: MessageCircle,
      color: 'from-blue-100 to-blue-200',
      description: 'It all started with a simple but ambitious goal: to revolutionize information access and streamline task resolution for our employees and guests. In late 2023, what began as a technical blueprint quickly grew into something more. We laid the foundation on the Google Cloud Platform and built an assistant designed to be the central point of contact for inquiries, saving valuable time for our teams.'
    },
    {
      id: 'language',
      title: 'Learning to Talk Your Language',
      icon: Brain,
      color: 'from-green-100 to-green-200',
      description: 'We knew Scout had to be more than just a search engine. It needed a personality. We trained Scout to be open, friendly, and helpful, with just the right amount of Strawberry sass. By integrating directly into the platforms our teams use every day like Google Chat and Slack, Scout became a true digital colleague, always ready to assist right where you work.'
    },
    {
      id: 'smarter',
      title: 'Growing Smarter, Together',
      icon: Users,
      color: 'from-purple-100 to-purple-200',
      description: 'Our journey has been one of continuous learning, driven by your feedback. Scout evolved from answering simple text questions to understanding complex attachments like images and documents. With every model upgrade, Scout has become more capable, helping with everything from writing emails in the Google Workspace side panel to managing support tickets with FreshService.'
    },
    {
      id: 'guests',
      title: 'Reaching Our Guests',
      icon: Globe,
      color: 'from-orange-100 to-orange-200',
      description: 'The vision for Scout always extended beyond our internal teams. We branched out a separate version of Scout for our guests, enabling our "More to explore" vision. Today, you can find custom AI assistants enhancing the guest experience at our beautiful NHR hotels, providing personalized recommendations and instant answers.'
    }
  ];

  const futureVision = [
    {
      id: 'guest-journey',
      title: 'A Seamless Guest Journey',
      icon: Heart,
      color: 'from-red-100 to-red-200',
      description: 'We envision Scout as a first-level customer support agent on our website, providing instant help and, if needed, seamlessly connecting guests to a human agent. We\'re also working on deep integrations with systems like Guesthouse to create agentic booking features.'
    },
    {
      id: 'creative-partner',
      title: 'A Creative Partner',
      icon: Palette,
      color: 'from-pink-100 to-pink-200',
      description: 'The future of Scout is not just about information, but inspiration. We\'re developing a mini-agent that will help our marketing and hotel teams generate on-brand, Strawberry-specific images, saving time and sparking creativity.'
    },
    {
      id: 'smarter-workplace',
      title: 'A Smarter Workplace',
      icon: BookOpen,
      color: 'from-emerald-100 to-emerald-200',
      description: 'We will continue to connect Scout to more of our core systems, from our Learning Management System, Motimate, to Salesforce. The goal is to make Scout the single, intelligent hub for getting work done efficiently and effectively across Strawberry.'
    }
  ];

  return (
    <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(/Gradient.avif)' }}>
      {/* Hero Section */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Hero Content */}
        <div className="relative z-10 text-center text-gray-900 px-6 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
            Meet Scout: More to Explore
          </h1>
          <p className="text-xl md:text-2xl font-text leading-relaxed max-w-3xl mx-auto">
            From a simple idea to an intelligent assistant, this is the story of how we're revolutionizing the way we work and connect with our guests.
          </p>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-900 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gray-900 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Story Blocks Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-display font-bold text-gray-900 mb-16 text-center">
            Our Journey: The Story of Scout
          </h2>
          
          <div className="space-y-20">
            {storyBlocks.map((block, index) => {
              const IconComponent = block.icon;
              const isEven = index % 2 === 0;
              
              return (
                                 <div 
                   key={block.id}
                   id={block.id}
                   className={`story-section flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-12 transition-all duration-1000 ${
                     seenSections.includes(block.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                   }`}
                 >
                  {/* Icon Side */}
                  <div className="flex-1 flex justify-center">
                    <div className={`w-32 h-32 bg-gradient-to-br ${block.color} rounded-3xl flex items-center justify-center shadow-2xl transform transition-all duration-500 hover:scale-110`}>
                      <IconComponent className="w-16 h-16 text-gray-700" />
                    </div>
                  </div>
                  
                  {/* Content Side */}
                  <div className="flex-1">
                    <h3 className="text-3xl font-display font-bold text-gray-900 mb-6">
                      {block.title}
                    </h3>
                    <p className="text-lg text-gray-600 font-text leading-relaxed">
                      {block.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Hotel Logo Carousel */}
      <HotelLogoCarousel />

      {/* Future Vision Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-display font-bold text-gray-900 mb-8 text-center">
            The Road Ahead: Our Vision for the Future
          </h2>
          <p className="text-xl text-gray-600 font-text text-center mb-16 max-w-3xl mx-auto">
            Our journey is far from over. We're constantly dreaming up new ways for Scout to empower our teams and delight our guests.
          </p>
          
          {/* Vision Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {futureVision.map((vision) => {
              const IconComponent = vision.icon;
              return (
                <Card key={vision.id} className="bg-white/90 backdrop-blur-xl border border-gray-200 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group">
                  <CardContent className="p-8">
                    {/* Icon */}
                    <div className={`w-20 h-20 bg-gradient-to-br ${vision.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg transform transition-all duration-300 group-hover:scale-110`}>
                      <IconComponent className="w-10 h-10 text-gray-700" />
                    </div>
                    
                    {/* Content */}
                    <div className="text-center">
                      <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                        {vision.title}
                      </h3>
                      <p className="text-gray-600 font-text leading-relaxed">
                        {vision.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Vision Summary */}
          <div className="mt-16 text-center">
            <div className="bg-white/60 backdrop-blur-xl rounded-2xl border border-gray-200 p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">
                Building the Future Together
              </h3>
              <p className="text-lg text-gray-600 font-text leading-relaxed">
                Each of these visions represents a step toward making Scout an even more integral part of how we work, 
                create, and serve our guests. We're excited to continue this journey and bring these innovations to life.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
